/**
 * German Holidays Module for jQuery UI Datepicker
 *
 * Features:
 * - Calculate fixed and movable German holidays
 * - Support for all 16 federal states (Bundeslaender)
 * - beforeShowDay callback for jQuery UI Datepicker
 * - Caching for performance
 *
 * @version 1.0.0
 */
(function($) {
    'use strict';

    var GermanHolidays = window.GermanHolidays = {
        // Current federal state (default: Bavaria)
        _currentState: 'BY',

        // Cache for calculated holidays: { 'BY_2025': { '2025-01-01': {...}, ... } }
        _cache: {},

        // Federal state codes and names
        STATES: {
            'BW': 'Baden-Württemberg',
            'BY': 'Bayern',
            'BE': 'Berlin',
            'BB': 'Brandenburg',
            'HB': 'Bremen',
            'HH': 'Hamburg',
            'HE': 'Hessen',
            'MV': 'Mecklenburg-Vorpommern',
            'NI': 'Niedersachsen',
            'NW': 'Nordrhein-Westfalen',
            'RP': 'Rheinland-Pfalz',
            'SL': 'Saarland',
            'SN': 'Sachsen',
            'ST': 'Sachsen-Anhalt',
            'SH': 'Schleswig-Holstein',
            'TH': 'Thüringen'
        },

        // Fixed holidays with federal state validity
        // Format: 'MM-DD': { name: '...', states: 'ALL' | ['BY', 'BW', ...] }
        FIXED_HOLIDAYS: {
            '01-01': { name: 'Neujahr', states: 'ALL' },
            '01-06': { name: 'Heilige Drei Könige', states: ['BW', 'BY', 'ST'] },
            '05-01': { name: 'Tag der Arbeit', states: 'ALL' },
            '08-15': { name: 'Mariä Himmelfahrt', states: ['BY', 'SL'] },
            '10-03': { name: 'Tag der Deutschen Einheit', states: 'ALL' },
            '10-31': { name: 'Reformationstag', states: ['BB', 'MV', 'SN', 'ST', 'TH', 'HH', 'SH', 'NI', 'HB'] },
            '11-01': { name: 'Allerheiligen', states: ['BW', 'BY', 'NW', 'RP', 'SL'] },
            '12-25': { name: '1. Weihnachtstag', states: 'ALL' },
            '12-26': { name: '2. Weihnachtstag', states: 'ALL' }
        },

        // Movable holidays (Easter-based)
        // offset: days from Easter Sunday
        MOVABLE_HOLIDAYS: {
            'karfreitag': { name: 'Karfreitag', offset: -2, states: 'ALL' },
            'ostersonntag': { name: 'Ostersonntag', offset: 0, states: 'ALL' },
            'ostermontag': { name: 'Ostermontag', offset: 1, states: 'ALL' },
            'himmelfahrt': { name: 'Christi Himmelfahrt', offset: 39, states: 'ALL' },
            'pfingstsonntag': { name: 'Pfingstsonntag', offset: 49, states: 'ALL' },
            'pfingstmontag': { name: 'Pfingstmontag', offset: 50, states: 'ALL' },
            'fronleichnam': {
                name: 'Fronleichnam',
                offset: 60,
                states: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL', 'SN', 'TH']
            }
        },

        /**
         * Set the current federal state
         * @param {string} stateCode - Two-letter state code (e.g., 'BY', 'NW')
         * @returns {boolean} - True if state was set successfully
         */
        setCurrentState: function(stateCode) {
            if (stateCode && this.STATES[stateCode]) {
                this._currentState = stateCode;
                return true;
            }
            return false;
        },

        /**
         * Get the current federal state
         * @returns {string} - Current state code
         */
        getCurrentState: function() {
            return this._currentState;
        },

        /**
         * Get state name by code
         * @param {string} stateCode
         * @returns {string|null}
         */
        getStateName: function(stateCode) {
            return this.STATES[stateCode] || null;
        },

        /**
         * Calculate Easter Sunday using Gaussian algorithm
         * @param {number} year - The year (e.g., 2025)
         * @returns {Date} - Date of Easter Sunday
         */
        calculateEasterSunday: function(year) {
            var a = year % 19;
            var b = Math.floor(year / 100);
            var c = year % 100;
            var d = Math.floor(b / 4);
            var e = b % 4;
            var f = Math.floor((b + 8) / 25);
            var g = Math.floor((b - f + 1) / 3);
            var h = (19 * a + b - d - g + 15) % 30;
            var i = Math.floor(c / 4);
            var k = c % 4;
            var l = (32 + 2 * e + 2 * i - h - k) % 7;
            var m = Math.floor((a + 11 * h + 22 * l) / 451);
            var month = Math.floor((h + l - 7 * m + 114) / 31) - 1; // 0-based
            var day = ((h + l - 7 * m + 114) % 31) + 1;

            return new Date(year, month, day);
        },

        /**
         * Calculate Buß- und Bettag (Repentance Day)
         * Wednesday before November 23rd
         * @param {number} year
         * @returns {Date}
         */
        calculateBussUndBettag: function(year) {
            var nov23 = new Date(year, 10, 23); // November = 10 (0-based)
            var dayOfWeek = nov23.getDay();
            // Wednesday = 3, so go back to previous Wednesday
            var daysBack = (dayOfWeek >= 3) ? (dayOfWeek - 3) : (dayOfWeek + 4);
            return new Date(year, 10, 23 - daysBack);
        },

        /**
         * Build holiday cache for a specific year and state
         * @param {string} stateCode
         * @param {number} year
         * @returns {Object} - Holiday map { 'YYYY-MM-DD': { name: '...', date: Date } }
         */
        _buildCache: function(stateCode, year) {
            var cacheKey = stateCode + '_' + year;
            if (this._cache[cacheKey]) {
                return this._cache[cacheKey];
            }

            var holidays = {};
            var self = this;

            // Fixed holidays
            $.each(this.FIXED_HOLIDAYS, function(dateStr, holiday) {
                if (self._isValidForState(holiday.states, stateCode)) {
                    var parts = dateStr.split('-');
                    var date = new Date(year, parseInt(parts[0]) - 1, parseInt(parts[1]));
                    var key = self._dateToKey(date);
                    holidays[key] = { name: holiday.name, date: date };
                }
            });

            // Movable holidays (Easter-based)
            var easter = this.calculateEasterSunday(year);
            $.each(this.MOVABLE_HOLIDAYS, function(id, holiday) {
                if (self._isValidForState(holiday.states, stateCode)) {
                    var date = new Date(easter);
                    date.setDate(date.getDate() + holiday.offset);
                    var key = self._dateToKey(date);
                    holidays[key] = { name: holiday.name, date: date };
                }
            });

            // Buß- und Bettag (only Saxony)
            if (stateCode === 'SN') {
                var bub = this.calculateBussUndBettag(year);
                var key = this._dateToKey(bub);
                holidays[key] = { name: 'Buß- und Bettag', date: bub };
            }

            this._cache[cacheKey] = holidays;
            return holidays;
        },

        /**
         * Check if holiday is valid for a specific state
         * @param {string|Array} states - 'ALL' or array of state codes
         * @param {string} stateCode
         * @returns {boolean}
         */
        _isValidForState: function(states, stateCode) {
            if (states === 'ALL') return true;
            return states.indexOf(stateCode) !== -1;
        },

        /**
         * Convert Date to string key (YYYY-MM-DD)
         * @param {Date} date
         * @returns {string}
         */
        _dateToKey: function(date) {
            var y = date.getFullYear();
            var m = ('0' + (date.getMonth() + 1)).slice(-2);
            var d = ('0' + date.getDate()).slice(-2);
            return y + '-' + m + '-' + d;
        },

        /**
         * Check if a date is a holiday
         * @param {Date} date
         * @param {string} [stateCode] - Optional, uses current state if not provided
         * @returns {Object|null} - Holiday info { name: '...', date: Date } or null
         */
        getHoliday: function(date, stateCode) {
            stateCode = stateCode || this._currentState;
            var year = date.getFullYear();
            var holidays = this._buildCache(stateCode, year);
            var key = this._dateToKey(date);
            return holidays[key] || null;
        },

        /**
         * Get all holidays for a year and state
         * @param {number} year
         * @param {string} [stateCode]
         * @returns {Object} - Holiday map
         */
        getHolidaysForYear: function(year, stateCode) {
            stateCode = stateCode || this._currentState;
            return this._buildCache(stateCode, year);
        },

        /**
         * jQuery UI Datepicker beforeShowDay callback for PICKUP dates only
         * Returns [selectable, cssClass, tooltip]
         * Note: Holiday warnings are only shown for pickup dates, not return dates
         *       (return is typically 24/7 via key drop-off)
         * @param {Date} date
         * @returns {Array}
         */
        beforeShowDay: function(date) {
            var holiday = GermanHolidays.getHoliday(date);
            if (holiday) {
                return [
                    true, // Selectable
                    'ui-datepicker-holiday', // CSS class
                    'Feiertag: ' + holiday.name + ' - Bitte beachten Sie mögliche Einschränkungen bei der Abholung'
                ];
            }
            return [true, '', ''];
        },

        /**
         * jQuery UI Datepicker beforeShowDay callback for RETURN dates
         * No holiday warnings for return (typically 24/7 key drop-off possible)
         * @param {Date} date
         * @returns {Array}
         */
        beforeShowDayReturn: function(date) {
            // No special styling for return dates - return is typically 24/7 possible
            return [true, '', ''];
        },

        /**
         * Get warning message for a holiday (pickup only)
         * @param {Date} date
         * @returns {string|null}
         */
        getWarningMessage: function(date) {
            var holiday = this.getHoliday(date);
            if (holiday) {
                return 'Hinweis: ' + holiday.name + ' - Bitte beachten Sie, dass die Abholung an Feiertagen möglicherweise eingeschränkt ist.';
            }
            return null;
        }
    };

    // Event listener for station selection
    $(document).on('stationSelected citySelected', function(e, data) {
        if (data && data.country_code === 'DE' && data.region_code) {
            if (GermanHolidays.setCurrentState(data.region_code)) {
                // Save to localStorage
                try {
                    localStorage.setItem('germanHolidays_state', data.region_code);
                } catch (e) {
                    // localStorage not available
                }

                // Refresh datepickers to update holiday display
                $('#anmietdatum, #rueckgabedatum, #nutz-anmietdatum, #nutz-rueckgabedatum').datepicker('refresh');
                $('#abhol, #rueck').datepicker('refresh');
            }
        }
    });

    // Initialize: Load saved state from localStorage and configure datepickers
    $(document).ready(function() {
        try {
            var savedState = localStorage.getItem('germanHolidays_state');
            if (savedState && GermanHolidays.STATES[savedState]) {
                GermanHolidays.setCurrentState(savedState);
            }
        } catch (e) {
            // localStorage not available
        }

        /**
         * Auto-configure beforeShowDay on datepickers
         * Wait for datepickers to be initialized (from bundle.min.js), then apply holiday callback
         * Note: Only PICKUP datepickers get holiday warnings (return is typically 24/7)
         */
        function configureHolidayDatepickers() {
            // Only pickup datepickers get holiday warnings
            var pickupSelectors = [
                '#anmietdatum',                              // Desktop main form pickup
                '#nutz-anmietdatum',                         // Desktop Nutzfahrzeuge form pickup
                '#abhol'                                     // Mobile form pickup
            ];

            $.each(pickupSelectors, function(i, selector) {
                var $dp = $(selector);
                // Check if element exists and has datepicker initialized
                if ($dp.length && $dp.hasClass('hasDatepicker')) {
                    $dp.datepicker('option', 'beforeShowDay', GermanHolidays.beforeShowDay);
                }
            });

            // Return datepickers don't need holiday warnings (24/7 key drop-off typically possible)
            // No special configuration needed - they use default datepicker behavior
        }

        // Try to configure immediately
        configureHolidayDatepickers();

        // Also try after a short delay (in case datepickers are initialized async)
        setTimeout(configureHolidayDatepickers, 100);
        setTimeout(configureHolidayDatepickers, 500);
    });

})(jQuery);
